import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Button,
  Fade,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Cookies from 'js-cookie';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUserCurrentQuery } from '../../redux/services/auth/auth.generated';
import {
  praharColorSelector,
  praharTextColorSelector,
} from '../../redux/slices/praharSlice';
import { BrowseCategoryType } from '../../redux/types.defined';
import StaticPageSideBar from '../SiteNavigationSidebar';
import AuthDropDown from './AuthDropDown';
import Breadcrumb from './Breadcrumb';
import NavTab from './NavTab';
import RagyaLogo from '/public/icons/ragya-logo.svg';
import RagyaLogoOrange from '/public/icons/ragya-logo-orange-svg.svg';
import { playingStateSelector } from '../../redux/slices/playerSlice';
import { PlayingStateEnum } from '../../enums/PlayerEnums';
import {
  playerModeSelector,
  setPlayerMode,
} from '../../redux/slices/playerSlice';
import { PlayingMode } from '../../redux/types.generated';
import PremiumFlower from '/public/icons/premium.png';
import Image from 'next/image';
import InstallPWA from './InstallPWA';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Avatar from '@mui/material/Avatar';
import {
  useGlobalSearchQuery,
  useLazyGlobalSearchQuery,
} from '../../redux/services/search/search.generated';
import CloseIcon from '@mui/icons-material/Close';

const praharRoute = '/prahar';
const favouritesRoute = '/favourites';

const goPremiumRoute = '/go-premium';
const forArtistsRoute = '/for-artists';

const browseRegex =
  /(\/ragas)|(\/instruments)|(\/artistes)|(\/browse)|(\/playlists)/;
const praharRegex = /(prahar)/;
const favouritesRegex = /(favourites)/;

const excludeSubscriptionBarRoute =
  /(\/gift)|(\/for-artists)|(\/go-premium)|(\/pricing)|(\/refunds-and-cancellations)|(\/terms-and-conditions)|(\/privacy-policy)|(\/disclaimer)/;

type activeTabType =
  | 'PRAHAR_TAB'
  | 'BROWSE_TAB'
  | 'FAVOURITES_TAB'
  | 'INACTIVE';

interface MenuBarProps {
  breakpoint: boolean;
  showNavTabs: boolean;
  showBreadcrumbs: boolean;
}

const MenuBar: FunctionComponent<MenuBarProps> = ({
  breakpoint,
  showNavTabs,
  showBreadcrumbs,
}) => {
  const router = useRouter();
  const currentPath = router.asPath;
  const theme = useTheme();
  const praharTextColor = useSelector(praharTextColorSelector);
  const isPraharPage = router.asPath.includes('/prahar');
  const praharTextColorValue = isPraharPage ? praharTextColor : '#fff';
  const grayTextRoutes = ['/redeem', '/login'];
  const isGrayTextPage = grayTextRoutes.includes(router.pathname);
  const unlockColor = isGrayTextPage
    ? theme.ragyaPalette.gray8
    : praharTextColorValue;

  const orangeLogoRoutes = [
    '/login',
    '/gift',
    '/redeem',
    '/refunds-and-cancellations',
    '/terms-and-conditions',
    '/privacy-policy',
    '/disclaimer',
  ];

  const menuIconRoutes = ['/go-premium', '/for-artists'];

  const isMenuIconRoute = menuIconRoutes.some((route) =>
    currentPath.startsWith(route)
  );

  const isOrangeLogoRoute = orangeLogoRoutes.some((route) =>
    currentPath.startsWith(route)
  );

  const isGradientBackgroundColorPage =
    currentPath === goPremiumRoute || currentPath === forArtistsRoute;

  const userToken = Cookies.get('ragya-token');

  const praharColor = useSelector(praharColorSelector) || 'transparent';
  // const browseRoute = useSelector(currentBrowseRouteSelector) ?? '/browse';
  const browseRoute = '/browse';

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const searchIconMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('sm')
  );

  const excludeSubscriptionBar = excludeSubscriptionBarRoute.test(currentPath);

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  // Use lazy so it only fires when you actually call it
  const [triggerSearch, { data: searchResults }] = useLazyGlobalSearchQuery();
  const anchorRef = useRef<HTMLDivElement>(null);

  // Whenever searchTerm changes, fire the query (you can debounce this if you like)
  useEffect(() => {
    if (searchTerm.trim()) {
      triggerSearch({ searchQuery: searchTerm });
    }
  }, [searchTerm, triggerSearch]);

  useEffect(() => {
    const hasAny =
      (searchResults?.artistList.data?.length ?? 0) +
        (searchResults?.instrumentList.data?.length ?? 0) +
        (searchResults?.ragaList.data?.length ?? 0) >
      0;

    setIsPopperOpen(searchTerm.trim() !== '' && hasAny);
  }, [searchTerm, searchResults]);

  useEffect(() => {
    setSearchTerm('');
    setMobileSearchOpen(false);
  }, [router.asPath]);

  const { data } = useUserCurrentQuery({}, { skip: !userToken });

  const [activeTab, setActiveTab] = useState<activeTabType>(
    currentPath === praharRoute
      ? 'PRAHAR_TAB'
      : currentPath === '/browse'
      ? 'BROWSE_TAB'
      : currentPath === favouritesRoute
      ? 'FAVOURITES_TAB'
      : 'INACTIVE'
  );

  const renderButton = (currentPath: string | BrowseCategoryType) => {
    if (breakpoint) {
      return (
        // <Link href={'/prahar'} passHref>
        //   <IconButton
        //     onClick={() => {
        //       setActiveTab('PRAHAR_TAB');
        //     }}
        //     sx={{
        //       '&:hover': {
        //         backgroundColor: 'transparent',
        //       },
        //     }}
        //     disableRipple
        //   >
        //     {breakpoint && <RagyaLogo height={32} />}
        //   </IconButton>
        // </Link>
        <></>
      );
    } else if (showNavTabs) {
      return (
        // <Typography
        //   sx={{
        //     fontFamily: 'Amulya, Serif',
        //     fontSize: '24px',
        //     fontWeight: 600,
        //     textTransform: 'capitalize',
        //   }}
        // >
        //   {currentPath.split('/')[1].split('?')[0]}
        // </Typography>
        <></>
      );
    } else {
      // TODO maybe ragya logo?
      return <></>;
    }
  };

  // now only exact matches:
  const isTransparentHeader =
    currentPath === '/prahar' ||
    currentPath === '/browse' ||
    currentPath === '/favourites';

  const termLower = searchTerm.trim().toLowerCase();

  const artistsRaw = searchResults?.artistList?.data ?? [];
  const instrumentsRaw = searchResults?.instrumentList?.data ?? [];
  const ragasRaw = searchResults?.ragaList?.data ?? [];

  // Helper: group into “starts with” and “others” via two filters
  function prioritizeByWord<T>(items: T[], keyFn: (item: T) => string): T[] {
    const lowTerm = termLower;
    const bucket1: T[] = []; // full-string prefix
    const bucket2: T[] = []; // any-word prefix
    const bucket3: T[] = []; // contains substring
    const bucket4: T[] = []; // the rest

    for (const item of items) {
      const text = keyFn(item).toLowerCase();
      if (text.startsWith(lowTerm)) {
        bucket1.push(item);
      } else {
        const words = text.split(/\s+/);
        const wordPrefix = words.some((w) => w.startsWith(lowTerm));
        if (wordPrefix) {
          bucket2.push(item);
        } else if (text.includes(lowTerm)) {
          bucket3.push(item);
        } else {
          bucket4.push(item);
        }
      }
    }
    return [...bucket1, ...bucket2, ...bucket3, ...bucket4];
  }

  const topArtistCandidates = prioritizeByWord(
    artistsRaw,
    (a) => `${a.firstName} ${a.lastName || ''}`
  );
  const topInstrumentCandidates = prioritizeByWord(
    instrumentsRaw,
    (i) => i.name
  );
  const topRagaCandidates = prioritizeByWord(ragasRaw, (r) => r.title);

  // 2) Slice to only show the first 3 in the dropdown
  const artists = topArtistCandidates.slice(0, 3);
  const instruments = topInstrumentCandidates.slice(0, 3);
  const ragas = topRagaCandidates.slice(0, 3);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        sx={{
          boxShadow: 'none',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-around',
          backgroundImage: 'none',
          backgroundColor: 'transparent',
        }}
        position={'fixed'}
      >
        <Toolbar
          variant={'dense'}
          disableGutters
          sx={{
            minHeight: '58px',
            px: isDesktop ? '16px' : 0,
            backgroundColor:
              isTransparentHeader && !isDesktop
                ? 'transparent'
                : isPraharPage
                ? praharColor
                : 'rgba(255,255,255,0.01)',
            backdropFilter:
              isTransparentHeader && !isDesktop ? 'none' : 'blur(15px)',
          }}
        >
          {isMenuIconRoute ? (
            <>
              <StaticPageSideBar
                userData={data}
                breakpoint={breakpoint}
                activeTab={activeTab}
                praharColor={praharColor}
              />
              <Link href='/prahar' passHref>
                <Box
                  component='a'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: 2,
                    pr: 2,
                    cursor: 'pointer',
                  }}
                >
                  <RagyaLogoOrange />
                </Box>
              </Link>
            </>
          ) : isOrangeLogoRoute ? (
            <Link href='/prahar' passHref>
              <Box
                component='a'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pl: 2,
                  pr: 2,
                  cursor: 'pointer',
                }}
              >
                <RagyaLogoOrange />
              </Box>
            </Link>
          ) : (
            <StaticPageSideBar
              userData={data}
              breakpoint={breakpoint}
              activeTab={activeTab}
              praharColor={praharColor}
            />
          )}
          {!searchIconMobile && !showBreadcrumbs && (
            <IconButton
              onClick={() => setMobileSearchOpen(true)}
              sx={{
                color: 'white',
                ml: 1,
                backgroundColor: alpha('#fff', 0.01),
                padding: '10px',
                backdropFilter: 'blur(15px)',
              }}
              aria-label='Open mobile search'
            >
              <SearchIcon />
            </IconButton>
          )}
          {/* ─── Mobile full‑screen search overlay ───────────────────────────── */}
          <Fade
            in={mobileSearchOpen}
            timeout={{ enter: 300, exit: 300 }}
            mountOnEnter
            unmountOnExit
          >
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: '#1D2121',
                zIndex: 1400,
                display: 'flex',
                flexDirection: 'column',
                pt: 1.5,
              }}
            >
              {/* top bar with back arrow and input */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pr: 2,
                  py: 1,
                }}
              >
                <IconButton
                  onClick={() => {
                    setMobileSearchOpen(false);
                    setSearchTerm('');
                  }}
                  sx={{
                    color: alpha('#fff', 0.5),
                    p: 0,
                    pl: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#fff',
                    },
                  }}
                  aria-label='Close search'
                >
                  <ChevronLeft sx={{ fontSize: 28 }} />
                </IconButton>
                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    ml: 0.6,
                    color: '#fff',
                    backgroundColor: alpha('#fff', 0.1),
                    borderRadius: '32px',
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <InputBase
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder='Search for artist, raga, instrument…'
                    sx={{
                      flex: 1,
                      fontSize: '0.9rem',
                      width: '93%',
                    }}
                    inputProps={{ 'aria-label': 'mobile search' }}
                  />
                  {searchTerm && (
                    <IconButton
                      size='small'
                      onClick={() => setSearchTerm('')}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                      }}
                      aria-label='Clear search'
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* reuse your desktop-popper content here */}
              {searchTerm.trim() !== '' && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    pb: '100px',
                  }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Amulya, serif',
                      }}
                      variant='subtitle1'
                    >
                      Top results
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: '#444' }} />

                  {/* ARTISTS */}
                  {artists.length > 0 && (
                    <Box sx={{ px: 2, pt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Amulya, serif',
                            letterSpacing: '3px',
                          }}
                          variant='overline'
                        >
                          ARTISTS
                        </Typography>
                        <Link href={`browse/artistes/`} passHref>
                          <Button
                            component='a'
                            size='small'
                            sx={{
                              color: '#aaa',
                              textTransform: 'none',
                              paddingLeft: '14px',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              },
                            }}
                          >
                            See all
                            <ChevronRight />
                          </Button>
                        </Link>
                      </Box>
                      <List disablePadding>
                        {artists.map((a) => (
                          <ListItem key={a.id} disablePadding>
                            <Link href={`browse/artistes/${a.id}`} passHref>
                              <Box
                                component='a'
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  py: 1,
                                  px: 2,
                                  textDecoration: 'none',
                                  color: '#fff',
                                }}
                              >
                                <Avatar
                                  src={a.profilePicture?.url}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    mr: 1.5,
                                    filter: 'grayscale(100%)',
                                  }}
                                />
                                <Typography>
                                  {a.firstName} {a.lastName}
                                </Typography>
                              </Box>
                            </Link>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* INSTRUMENTS (same pattern) */}
                  {instruments.length > 0 && (
                    <Box sx={{ px: 2, pt: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Amulya, serif',
                            letterSpacing: '3px',
                          }}
                          variant='overline'
                        >
                          INSTRUMENTS
                        </Typography>
                        <Link href={`/browse/instruments/`} passHref>
                          <Button
                            component='a'
                            size='small'
                            sx={{
                              color: '#aaa',
                              textTransform: 'none',
                              paddingLeft: '14px',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              },
                            }}
                          >
                            See all
                            <ChevronRight />
                          </Button>
                        </Link>
                      </Box>
                      <List disablePadding>
                        {instruments.map((i) => (
                          <ListItem key={i.id} disablePadding>
                            <Link href={`/browse/instruments/${i.id}`} passHref>
                              <Box
                                component='a'
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  py: 1,
                                  px: 2,
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  '&:hover': {
                                    backgroundColor:
                                      'rgba(255, 255, 255, 0.05)',
                                  },
                                }}
                              >
                                <Avatar
                                  src={i.profilePicture?.url}
                                  sx={{ width: 32, height: 32, mr: 1.5 }}
                                />
                                <Typography>{i.name}</Typography>
                              </Box>
                            </Link>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* RAGAS (same pattern) */}
                  {ragas.length > 0 && (
                    <Box sx={{ px: 2, pt: 1, pb: 4 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Amulya, serif',
                            letterSpacing: '3px',
                          }}
                          variant='overline'
                        >
                          RAGAS
                        </Typography>
                        <Link href={`/browse/ragas/`} passHref>
                          <Button
                            component='a'
                            size='small'
                            sx={{
                              color: '#aaa',
                              textTransform: 'none',
                              paddingLeft: '14px',
                              borderRadius: '20px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              },
                            }}
                          >
                            See all
                            <ChevronRight />
                          </Button>
                        </Link>
                      </Box>
                      <List disablePadding>
                        {ragas.map((r) => {
                          const thumbUrl =
                            r.timeSections?.[0]?.profilePicture?.url;
                          return (
                            <ListItem disablePadding key={r.id}>
                              <Link href={`/browse/ragas/${r.id}`} passHref>
                                <Box
                                  component='a'
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    py: 1,
                                    px: 2,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    '&:hover': {
                                      backgroundColor:
                                        'rgba(255, 255, 255, 0.05)',
                                    },
                                  }}
                                >
                                  {thumbUrl && (
                                    <Avatar
                                      src={thumbUrl}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        mr: 1.5,
                                      }}
                                    />
                                  )}
                                  <Typography>{r.title}</Typography>
                                </Box>
                              </Link>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Fade>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {showBreadcrumbs ? (
              <Breadcrumb path={currentPath} />
            ) : (
              renderButton(currentPath)
            )}
          </Box>

          <Box sx={{ display: 'flex', flexGrow: 1, height: '100%' }}>
            {showNavTabs && breakpoint && (
              <Box
                sx={{
                  display: 'flex',
                  // flexGrow: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  // width: '480px',
                  height: '100%',
                }}
              >
                <NavTab
                  sx={{
                    backdropFilter: 'none',
                    py: 1,
                    color: praharTextColorValue,
                  }}
                  active={praharRegex.test(currentPath)}
                  href={praharRoute}
                  label='Prahar'
                />
                <NavTab
                  sx={{
                    backdropFilter: 'none',
                    py: 1,
                    color: praharTextColorValue,
                  }}
                  active={favouritesRegex.test(currentPath)}
                  href={favouritesRoute}
                  label='Favourites'
                />
                <NavTab
                  sx={{
                    backdropFilter: 'none',
                    py: 1,
                    color: praharTextColorValue,
                  }}
                  active={browseRegex.test(currentPath)}
                  href={browseRoute}
                  label='Browse'
                />
                <Box
                  ref={anchorRef}
                  sx={{
                    position: 'relative',
                    borderRadius: '32px',
                    backgroundColor: alpha('#fff', 0.1),
                    '&:hover, &:focus-within': {
                      backgroundColor: alpha('#fff', 0.2),
                    },
                    ml: 1,
                    width: '500px',
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    px: 1,
                  }}
                >
                  <SearchIcon sx={{ mr: 1, color: 'inherit' }} />
                  <InputBase
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder='Search for artist, raga, instrument, playlist, renditions'
                    inputProps={{ 'aria-label': 'search' }}
                    sx={{
                      width: '100%',
                      fontFamily: 'Amulya, Serif',
                      '& .MuiInputBase-input': {
                        color: '#fff',
                        fontSize: '1rem',
                        padding: 0,
                        '&::placeholder': {
                          // 80% opacity white for placeholder
                          color: alpha('#fff', 0.8),
                        },
                      },
                    }}
                  />
                </Box>
                <Popper
                  open={isPopperOpen}
                  anchorEl={anchorRef.current}
                  placement='bottom-start'
                  style={{ zIndex: 1300 }}
                >
                  <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
                    <Paper
                      sx={{
                        mt: 1,
                        width: anchorRef.current?.offsetWidth,
                        bgcolor: '#1d2121',
                        color: '#fff',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ px: 2, py: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Amulya, serif',
                          }}
                          variant='subtitle1'
                        >
                          Top results
                        </Typography>
                      </Box>
                      <Divider sx={{ borderColor: '#444' }} />

                      {/* ARTISTS */}
                      {artists.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 2,
                              pt: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Amulya, serif',
                                letterSpacing: '3px',
                              }}
                              variant='overline'
                            >
                              ARTISTS
                            </Typography>
                            <Link href={`browse/artistes/`} passHref>
                              <Button
                                component='a'
                                size='small'
                                sx={{
                                  color: '#aaa',
                                  textTransform: 'none',
                                  paddingLeft: '14px',
                                  borderRadius: '20px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                See all
                                <ChevronRight />
                              </Button>
                            </Link>
                          </Box>
                          <List disablePadding>
                            {artists.map((a) => (
                              <ListItem key={a.id} disablePadding>
                                <Link href={`browse/artistes/${a.id}`} passHref>
                                  <Box
                                    component='a'
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      py: 1,
                                      px: 2,
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(255, 255, 255, 0.05)',
                                      },
                                    }}
                                  >
                                    <Avatar
                                      src={a.profilePicture?.url}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        mr: 1.5,
                                        filter: 'grayscale(100%)',
                                      }}
                                    />
                                    <Typography>
                                      {a.firstName} {a.lastName}
                                    </Typography>
                                  </Box>
                                </Link>
                              </ListItem>
                            ))}
                          </List>
                          <Divider sx={{ borderColor: '#444' }} />
                        </>
                      )}

                      {/* INSTRUMENTS */}
                      {instruments.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 2,
                              pt: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Amulya, serif',
                                letterSpacing: '3px',
                              }}
                              variant='overline'
                            >
                              INSTRUMENTS
                            </Typography>
                            <Link href={`/browse/instruments/`} passHref>
                              <Button
                                component='a'
                                size='small'
                                sx={{
                                  color: '#aaa',
                                  textTransform: 'none',
                                  paddingLeft: '14px',
                                  borderRadius: '20px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                See all
                                <ChevronRight />
                              </Button>
                            </Link>
                          </Box>
                          <List disablePadding>
                            {instruments.map((i) => (
                              <ListItem key={i.id} disablePadding>
                                <Link
                                  href={`/browse/instruments/${i.id}`}
                                  passHref
                                >
                                  <Box
                                    component='a'
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      width: '100%',
                                      py: 1,
                                      px: 2,
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(255, 255, 255, 0.05)',
                                      },
                                    }}
                                  >
                                    <Avatar
                                      src={i.profilePicture?.url}
                                      sx={{ width: 32, height: 32, mr: 1.5 }}
                                    />
                                    <Typography>{i.name}</Typography>
                                  </Box>
                                </Link>
                              </ListItem>
                            ))}
                          </List>
                          <Divider sx={{ borderColor: '#444' }} />
                        </>
                      )}

                      {/* RAGAS */}
                      {ragas.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 2,
                              pt: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Amulya, serif',
                                letterSpacing: '3px',
                              }}
                              variant='overline'
                            >
                              RAGAS
                            </Typography>
                            <Link href={`/browse/ragas/`} passHref>
                              <Button
                                component='a'
                                size='small'
                                sx={{
                                  color: '#aaa',
                                  textTransform: 'none',
                                  paddingLeft: '14px',
                                  borderRadius: '20px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  },
                                }}
                              >
                                See all
                                <ChevronRight />
                              </Button>
                            </Link>
                          </Box>
                          <List disablePadding>
                            {ragas.map((r) => {
                              const thumbUrl =
                                r.timeSections?.[0]?.profilePicture?.url;
                              return (
                                <ListItem disablePadding key={r.id}>
                                  <Link href={`/browse/ragas/${r.id}`} passHref>
                                    <Box
                                      component='a'
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        py: 1,
                                        px: 2,
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        '&:hover': {
                                          backgroundColor:
                                            'rgba(255, 255, 255, 0.05)',
                                        },
                                      }}
                                    >
                                      {thumbUrl && (
                                        <Avatar
                                          src={thumbUrl}
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            mr: 1.5,
                                          }}
                                        />
                                      )}
                                      <Typography>{r.title}</Typography>
                                    </Box>
                                  </Link>
                                </ListItem>
                              );
                            })}
                          </List>
                          {/* <Divider sx={{ borderColor: '#444' }} /> */}
                        </>
                      )}

                      {/* You can extend similarly for renditions once you add them to the query */}
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Box>
            )}
          </Box>
          <InstallPWA isDesktop={isDesktop} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              pr: isDesktop ? 0.5 : 0.5,
            }}
          >
            {isDesktop &&
              !data?.userCurrent?.activeSubscription?.id &&
              !excludeSubscriptionBar && (
                <Link passHref href='/go-premium'>
                  <Button
                    component='a'
                    target='_blank'
                    rel='noopener noreferrer'
                    size='small'
                    sx={{
                      color: 'white',
                      borderRadius: '20px',
                      textTransform: 'none',
                      px: 2,
                      py: 0.5,
                      backdropFilter: 'blur(15px)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: '500',
                        pr: '10px',
                        color: unlockColor,
                      }}
                    >
                      Unlock Features
                    </Typography>
                    <Image
                      src={PremiumFlower}
                      alt='PremiumFlower'
                      width={18}
                      height={18}
                    />
                  </Button>
                </Link>
              )}
            <AuthDropDown
              breakpoint={breakpoint}
              isGradientBackground={isGradientBackgroundColorPage}
              activeTab={activeTab}
            />
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default MenuBar;
